import { accountNumber, routingNumber } from 'us-bank-account-validator'
import { get, isEmpty, isNil, isString, omit } from 'lodash'
import type { FieldValues, FormState, Path, UseFormWatch, ValidationRule } from 'react-hook-form'
import { isObject } from './objectUtils'
import { useTranslations } from 'next-intl'
import { removeWhiteSpace } from './stringUtils'

export type InputStates = 'complete' | 'error' | undefined

const resolveHasValue = (value: unknown): boolean => {
    if (isNil(value)) return false
    if (isString(value) && isEmpty(value)) return false

    return true
}

export function resolveInputState(value: unknown, error: undefined | string | Record<string, unknown>): InputStates {
    const hasValue = resolveHasValue(value)
    const isError = !isNil(error)

    if (isError) {
        return 'error'
    }

    if (hasValue && !isError) {
        return 'complete'
    }

    return undefined
}

export function hasValuesInForm<T extends FieldValues>(values: T): boolean {
    const formValues = Object.values(values)

    return !isEmpty(
        formValues
            .filter((value) => !isNil(value))
            .filter((value) => {
                if (isObject(value)) {
                    const definedValues = Object.values(value)
                        .filter((value) => !isNil(value))
                        .filter((value) => !isEmpty(value))
                    return !isEmpty(definedValues)
                }

                return !isEmpty(value)
            })
    )
}

export const inputStateContext = <T extends FieldValues>(watch: UseFormWatch<T>, errors: FormState<T>['errors']) => {
    return (key: Path<T>) => {
        return resolveInputState(watch(key), get(errors, key))
    }
}

export const inputMessageContext = <T extends FieldValues>(errors: FormState<T>['errors']) => {
    return (key: Path<T>): string | undefined => {
        const message: unknown = get(errors, key)?.message
        return typeof message === 'string' ? message : undefined
    }
}

export const useInputRequiredContext = (): ((condition?: boolean) => ValidationRule<boolean>) => {
    const t = useTranslations('root')
    return (condition?: boolean) => ({ value: condition ?? true, message: t('errors.inputs.required') })
}

export function removeEmailWhitespace<T extends { email: string }>(obj: T): T {
    return {
        ...obj,
        email: removeWhiteSpace(obj.email)
    }
}

export const isValidUKAccountNumber = (accountNumber: string): boolean => {
    const cleanAccountNumber = accountNumber.replace(/[-\s]/g, '')
    return /^[0-9]{7,8}$/.test(cleanAccountNumber)
}

export const isValidSortCode = (sortCode: string): boolean => {
    const cleanSortCode = sortCode.replace(/[-\s]/g, '')
    return /^[0-9]{6}$/.test(cleanSortCode)
}

export const isValidUSAccountNumber = (input: string): boolean => {
    const clean = input.replace(/\s+/g, '')
    const parts = clean.split(/[/|]/)

    const accountPart = parts.length === 2 ? parts[1] : parts[0]
    if (!/^\d{7,17}$/.test(accountPart)) return false

    if (parts.length === 2) {
        const [routing, account] = parts
        return routingNumber(routing).isValid && accountNumber(account).isValid
    }

    return accountNumber(accountPart).isValid
}

export const isValidAccountNumber = (input: string): boolean => {
    return isValidUKAccountNumber(input) || isValidUSAccountNumber(input)
}

/**
 * Strips the chrome the shared useForm wrapper injects into every register()
 * return, so what is left is only the props a real control accepts.
 *
 * The wrapper adds state, message and required so the legacy makalu inputs can
 * draw their own label and error. A @hello-aria/ui control puts that chrome in
 * Field instead, and spreading the three onto it lands them on the DOM node as
 * unknown attributes while the error silently stops rendering.
 */
export const FORM_FIELD_CHROME = ['state', 'message', 'required'] as const

export const toControlProps = <TField extends Record<string, unknown>>(field: TField) => omit(field, FORM_FIELD_CHROME)
